<template>
  <ts-card>
    <div
      class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative"
    >
      <h4 class="ts-text-sm">{{ $t('approvalWorkFlow.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-loading-banner :loading="loadingPreData">
      <ts-card-wrapper>
        <div class="row">
          <div class="col-lg-5">
            <div class="form-group">
              <label class="col-form-label required">{{
                $t('approvalWorkFlow.approvalType')
              }}</label>
              <select
                class="form-control"
                v-model.number="model.app_type_id"
                @change="fetchParam"
                :class="{ 'is-invalid': errors.has('app_type_id') }"
              >
                <option value>{{ $t('select') }}</option>
                <option
                  v-for="(app_type, index) in approvalTypes"
                  :key="index"
                  :value="app_type.app_type_id"
                  >{{ app_type.app_type }}</option
                >
              </select>
              <div class="invalid-feedback" v-if="errors.has('app_type_id')">
                {{ errors.first('app_type_id') }}
              </div>
            </div>
            <div class="row form-group">
              <div class="col-lg-6">
                <label class="col-form-label">
                  {{ $t('approvalWorkFlow.escalateAfterHours') }}
                </label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="model.escalate_after_xx_hours"
                />
              </div>
              <div class="col-lg-6">
                <label class="col-form-label">
                  {{ $t('approvalWorkFlow.escalateTimes') }}
                </label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="model.escalate_times"
                />
              </div>
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalWorkFlow.approverNotificationTemplate') }}
              </label>
              <textarea
                rows="2"
                class="form-control"
                v-model="model.approver_notification_template"
              ></textarea>
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalWorkFlow.initiatorNotificationTemplate') }}
              </label>
              <textarea
                rows="3"
                class="form-control"
                v-model="model.initiator_notification_template"
              ></textarea>
            </div>
            <div class="form-group">
              <label class="col-form-label"></label>
              <ts-checkbox v-model="model.is_enabled">{{
                $t('approvalWorkFlow.isEnabled')
              }}</ts-checkbox>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalWorkFlow.emailSubject') }}
              </label>
              <input
                type="text"
                class="form-control"
                v-model="model.email_subject"
              />
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalWorkFlow.approverEmailTemplate') }}
              </label>
              <textarea
                style="height: 130px; margin-top: 0px; margin-bottom: 0px;"
                class="form-control"
                v-model="model.approver_email_template"
              ></textarea>
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalWorkFlow.initiatorEmailTemplate') }}
              </label>
              <textarea
                rows="3"
                class="form-control"
                v-model="model.initiator_email_template"
              ></textarea>
            </div>
          </div>
          <div class="col-lg-3">
            <div class="note note-primary note-border-top">
              <div class="note-content text-center">
                <h4>
                  <b>
                    {{ $t('approvalWorkFlow.parameters') }}
                    <i class="fas fa-spinner spin" v-if="loadParameter"></i>
                  </b>
                  <i
                    class="fas fa-question-circle ml-2"
                    v-tooltip="
                      $t('approvalWorkFlow.useTheseParametersToCreateTemplate')
                    "
                    v-if="!loadParameter"
                  ></i>
                </h4>
                <div class="m-auto">
                  <div v-for="(param, index) in parameters" :key="index">
                    <ul>
                      <li
                        class="parameter"
                        v-clipboard:copy="param.param_name"
                        v-clipboard:success="onCopy"
                        v-clipboard:error="onError"
                        v-tooltip="copied"
                      >
                        {{ param.param_name }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ts-card-wrapper>
      <hr style="height: 1px" />
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-12 text-right">
            <ts-button
              class="btn-default mr-2"
              @click.prevent="$emit('close')"
              >{{ $t('cancel') }}</ts-button
            >
            <ts-button
              class="btn-primary"
              @click.prevent="update"
              :waiting="saving"
              >{{ $t('save') }}</ts-button
            >
          </div>
        </div>
      </ts-card-wrapper>
    </ts-loading-banner>
  </ts-card>
</template>

<script>
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'

export default {
  name: 'approvalWorkFlow',
  props: ['editData'],
  data() {
    return {
      model: {
        app_flow_id: 0,
        app_type_id: '',
        escalate_after_xx_hours: '',
        is_enabled: true,
        escalate_times: '',
        approver_notification_template: '',
        initiator_notification_template: '',
        email_subject: '',
        approver_email_template: '',
        initiator_email_template: ''
      },
      copied: this.$t('approvalWorkFlow.copyParameters'),
      errors: new Errors(),
      saving: false,
      loadingPreData: false,
      loadParameter: false
    }
  },
  created() {
    this.fetchResource()
  },
  computed: {
    ...mapState('approvalWorkFlow', ['approvalTypes', 'parameters'])
  },
  methods: {
    fetchResource() {
      this.loadingPreData = true
      this.$store
        .dispatch('approvalWorkFlow/fetchResource')
        .then(() => {
          this.loadingPreData = false
          this.setEdit(this.editData)
        })
        .catch(error => {
          this.loadingPreData = false
          this.$toasted.error(error.message)
        })
    },
    fetchParam() {
      this.loadParameter = true
      this.$store
        .dispatch('approvalWorkFlow/fetchParameter', this.model.app_type_id)
        .then(() => {
          this.loadParameter = false
        })
        .catch(error => {
          this.loadParameter = false
          this.$toasted.error(error.message)
        })
    },
    onCopy: function(e) {
      this.copied = this.$t('approvalWorkFlow.copied')
      setTimeout(() => {
        this.copied = this.$t('approvalWorkFlow.copyParameters')
      }, 1000)
    },
    onError: function(e) {
      this.copied = this.$t('approvalWorkFlow.failedCopied')
    },
    update() {
      this.errors = new Errors()
      this.saving = true
      this.$store
        .dispatch('approvalWorkFlow/update', {
          app_flow_id: this.editData.app_flow_id,
          ...this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.saving = false
          this.$emit('close')
        })
        .catch(error => {
          this.saving = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    setEdit(data) {
      if (data === null || data === undefined) return

      this.model.app_flow_id = data.app_flow_id
      this.model.app_type_id = data.app_type_id
      this.model.escalate_after_xx_hours = data.escalate_after_xx_hours
      this.model.is_enabled = data.is_enabled
      this.model.escalate_times = data.escalate_times
      this.model.approver_notification_template =
        data.approver_notification_template
      this.model.initiator_notification_template =
        data.initiator_notification_template
      this.model.email_subject = data.email_subject
      this.model.approver_email_template = data.approver_email_template
      this.model.initiator_email_template = data.initiator_email_template
    }
  },
  watch: {
    'model.app_type_id': function(value) {
      if (value) {
        this.fetchParam()
      }
    }
  }
}
</script>
<style lang="css" scoped>
.parameter {
  cursor: pointer;
  text-align: center;
}
.note-border-top {
  border-top: 3px solid #348fe2;
  border-left: none;
}
li {
  padding: 5px 0;
}
</style>
